import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { GovernancePieSector } from "../regulation/GovernancePieSector";
import { ChartTooltipContent } from "../ui/chart";
import { paletteColor, type FigurePalette } from "./mermaidSource";
import { pieShareLabel, pieTotal, type PieChartSpec } from "./pieChart";

/** Same accent ramp as the regulation dashboard donut, extended for more slices. */
const SLICE_COLORS: ReadonlyArray<string> = [
  "var(--color-primary)",
  "color-mix(in oklab, var(--color-primary) 70%, var(--color-card))",
  "color-mix(in oklab, var(--color-primary) 45%, var(--color-card))",
  "color-mix(in oklab, var(--color-primary) 25%, var(--color-card))",
  "var(--color-muted-foreground)",
  "color-mix(in oklab, var(--color-muted-foreground) 60%, var(--color-card))",
  "color-mix(in oklab, var(--color-muted-foreground) 35%, var(--color-card))",
];

function sliceColor(index: number, palette: FigurePalette | null): string {
  return paletteColor(palette, index, SLICE_COLORS);
}

/**
 * Interactive rendering of a mermaid `pie` fence: a donut with hover values
 * beside a legend that carries each slice's value and share, so the numbers
 * are readable without hovering.
 */
export function PieFigure({ spec }: { readonly spec: PieChartSpec }) {
  const total = pieTotal(spec);
  const slices = spec.slices.map((slice, index) => ({
    key: `${index}-${slice.label}`,
    label: slice.label,
    value: slice.value,
    color: sliceColor(index, spec.palette),
  }));
  const drawn = slices.filter((slice) => slice.value > 0);

  return (
    <div className="chat-markdown-pie">
      {spec.title ? <div className="chat-markdown-xychart-title">{spec.title}</div> : null}
      <div className="chat-markdown-pie-body">
        {/* The legend list is the accessible form; only the donut is an image. */}
        <div className="chat-markdown-pie-donut" role="img" aria-label={spec.title ?? "Pie chart"}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={drawn}
                dataKey="value"
                nameKey="label"
                innerRadius="62%"
                outerRadius="100%"
                paddingAngle={drawn.length > 1 ? 2 : 0}
                cornerRadius={3}
                stroke="var(--color-card)"
                strokeWidth={1}
                isAnimationActive={false}
                shape={GovernancePieSector}
              >
                {drawn.map((slice) => (
                  <Cell key={slice.key} fill={slice.color} />
                ))}
              </Pie>
              <Tooltip wrapperStyle={{ zIndex: 40 }} content={<ChartTooltipContent hideLabel />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="chat-markdown-pie-legend">
          {slices.map((slice) => (
            <li key={slice.key}>
              <span aria-hidden className="chat-markdown-pie-swatch" style={{ background: slice.color }} />
              <span className="chat-markdown-pie-label">{slice.label}</span>
              <span className="chat-markdown-pie-value">
                {spec.showData ? `${slice.value} · ` : ""}
                {pieShareLabel(slice.value, total)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
